import React, { useState, useRef, useEffect, useCallback } from 'react';
import { prepareImageForUpload } from '../utils/imageCompress';
import { supabase } from '../supabase';
import { getChatRequestUrl } from '../config';
import { Package2 } from 'lucide-react';
import RoutineAnalysisModal from './RoutineAnalysisModal';

// ── Constants ─────────────────────────────────────────────────────────────────
const STEP_CATEGORIES = [
  { id: 'cleanser',    label: 'Cleanser',    emoji: '🧼' },
  { id: 'toner',       label: 'Toner',       emoji: '💧' },
  { id: 'serum',       label: 'Serum',       emoji: '✨' },
  { id: 'eye_cream',   label: 'Eye Cream',   emoji: '👁' },
  { id: 'moisturiser', label: 'Moisturiser', emoji: '🫧' },
  { id: 'spf',         label: 'SPF',         emoji: '☀️' },
  { id: 'treatment',   label: 'Treatment',   emoji: '🧪' },
  { id: 'oil',         label: 'Face Oil',    emoji: '💛' },
  { id: 'mask',        label: 'Mask',        emoji: '🫙' },
  { id: 'other',       label: 'Other',       emoji: '➕' },
];

const FREQUENCIES = ['Daily', 'AM only', 'PM only', 'Every other day', '2–3x / week', 'Weekly', 'As needed'];

const NAME_SUGGESTIONS = ['Exfoliation', 'Barrier Repair', 'Retinol', 'Travel Kit', 'Post-Procedure', 'Minimal Routine'];

// Strip pack-size / quantity suffixes from a product title ("… 50mL*2ea" -> "…").
const stripPackSuffix = (name) => String(name || '')
  .replace(/\s*\d+(\.\d+)?\s*(ml|g|oz|fl\.?\s*oz|kg|mg|l)\b\s*([*x×]\s*\d+\s*(ea|pcs?|pack)?)?/gi, '')
  .replace(/\s{2,}/g, ' ')
  .trim();

// Map catalog category strings → our categoryId values
const CATALOG_CATEGORY_MAP = {
  'moisturizer': 'moisturiser', 'moisturiser': 'moisturiser',
  'cleanser': 'cleanser', 'face wash': 'cleanser',
  'toner': 'toner',
  'serum': 'serum', 'essence': 'serum',
  'eye cream': 'eye_cream', 'eye_cream': 'eye_cream',
  'sunscreen': 'spf', 'spf': 'spf', 'sun protection': 'spf',
  'treatment': 'treatment', 'retinol': 'treatment', 'exfoliator': 'treatment',
  'face oil': 'oil', 'oil': 'oil', 'facial oil': 'oil',
  'mask': 'mask', 'face mask': 'mask',
};

function makeStep(categoryId) {
  return {
    id: `${categoryId}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    categoryId,
    productName: '',
    brand: '',
    frequency: 'Daily',
  };
}

function getCat(id) {
  return STEP_CATEGORIES.find(c => c.id === id) || STEP_CATEGORIES[STEP_CATEGORIES.length - 1];
}

// Daily routine = always has AM/PM split via hasTimeOfDay: true
const INITIAL_ROUTINES = [
  {
    id: 'daily',
    name: 'Daily',
    isDaily: true,
    hasTimeOfDay: true,
    morningSteps: ['cleanser', 'toner', 'serum', 'moisturiser', 'spf'].map(makeStep),
    eveningSteps: ['cleanser', 'toner', 'serum', 'moisturiser'].map(makeStep),
  },
];

// ── Step edit modal ────────────────────────────────────────────────────────────
// "My Products" tab searches both saved products and the full product catalog.
function StepModal({ step, userId, savedProducts, onConfirm, onCancel }) {
  const isEditing = !!step?.productName;
  const [productName, setProductName] = useState(step?.productName || '');
  const [brand, setBrand] = useState(step?.brand || '');
  const [categoryId, setCategoryId] = useState(step?.categoryId || 'cleanser');
  const [imageRemoved, setImageRemoved] = useState(!!step?.imageRemoved);
  const [imageUrl, setImageUrl] = useState(() => {
    // Distinguish three states:
    //   - non-empty string → use this image
    //   - empty string ''  → user explicitly cleared (sentinel)
    //   - undefined        → never set, pre-populate from savedProducts catalog match
    if (step && typeof step.imageUrl === 'string') return step.imageUrl;
    if (step?.productName && savedProducts?.length) {
      const match = savedProducts.find(p =>
        p.name === step.productName && (!step.brand || p.brand === step.brand)
      );
      if (match) return match.catalog_image_url || match.image_url || match.image || '';
    }
    return '';
  });
  const [view, setView] = useState(isEditing || !savedProducts.length ? 'manual' : 'catalog');
  const [searchQuery, setSearchQuery] = useState('');
  const [catalogResults, setCatalogResults] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const searchTimerRef = useRef(null);
  // Inline catalog suggestions for the manual-entry Product name field.
  // Independent of the "My Products" catalog tab so the two flows don't clash.
  const [manualSuggestions, setManualSuggestions] = useState([]);
  const [manualSuggestionsOpen, setManualSuggestionsOpen] = useState(false);
  const manualSearchTimerRef = useRef(null);

  // Custom product image upload — lets users add their own photo for a
  // routine step when the catalog doesn't have a hero or got the wrong one.
  // Two inputs (camera-direct + library) so Android users actually get the
  // Take Photo option; iOS works with either.
  const productImageInputRef = useRef(null);
  const productImageCameraRef = useRef(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);

  const handleProductImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!userId) {
      setImageUploadError('Sign in to upload a photo for this product.');
      return;
    }
    setImageUploading(true);
    setImageUploadError(null);
    try {
      // Compress before upload — product photos can be 5MB+ off a modern
      // phone camera, which we don't need to store at full res for a 36px
      // thumbnail. prepareImageForUpload returns { compressedBlob, base64 };
      // the Blob is fine for Supabase storage upload.
      const { compressedBlob } = await prepareImageForUpload(file);
      const safeName = ((compressedBlob.name) || file.name || 'product.jpg').replace(/[^a-z0-9.]/gi, '_');
      const filePath = `routine_images/${userId}/${Date.now()}_${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, compressedBlob, {
          contentType: compressedBlob.type || file.type || 'image/jpeg',
          upsert: false,
        });
      if (uploadError) throw uploadError;
      // 7-day signed URL. The routine-load path already refreshes any
      // expired signed URLs on saved products; the same pattern handles
      // these once we extend it (follow-up task).
      const { data, error: signError } = await supabase.storage
        .from('images')
        .createSignedUrl(filePath, 604800);
      if (signError || !data?.signedUrl) throw signError || new Error('No signed URL');
      setImageUrl(data.signedUrl);
      setImageRemoved(false);
    } catch (err) {
      console.error('Product image upload error:', err);
      setImageUploadError('Could not upload photo. Try again.');
    } finally {
      setImageUploading(false);
    }
  };

  const handleClearProductImage = () => {
    setImageUrl('');
    setImageRemoved(true);
    setImageUploadError(null);
  };

  // Debounced catalog search (only fires when query >= 2 chars)
  useEffect(() => {
    if (view !== 'catalog') return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (searchQuery.trim().length < 2) {
      setCatalogResults([]);
      setCatalogLoading(false);
      return;
    }

    setCatalogLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('products')
          .select('id, name, brand, image_url, category')
          .or(`name.ilike.%${searchQuery}%,brand.ilike.%${searchQuery}%`)
          .limit(20);
        setCatalogResults(data || []);
      } catch {
        setCatalogResults([]);
      } finally {
        setCatalogLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery, view]);

  const savedFiltered = (savedProducts || []).filter(p => {
    const q = searchQuery.toLowerCase();
    return !q || (p.name || '').toLowerCase().includes(q) || (p.brand || '').toLowerCase().includes(q);
  });

  const handleSelectProduct = (p, catId) => {
    setProductName(p.name || '');
    setBrand(p.brand || '');
    if (catId) setCategoryId(catId);
    setImageUrl(p.catalog_image_url || p.image_url || p.image || '');
    setImageRemoved(false);
    setView('manual');
  };

  // Manual-entry inline search: type a product name → debounced catalog query
  // → suggestions dropdown. Picking one fills name + brand.
  const handleManualNameChange = (value) => {
    setProductName(value);
    setManualSuggestionsOpen(true);
    if (manualSearchTimerRef.current) clearTimeout(manualSearchTimerRef.current);
    if (value.trim().length < 2) {
      setManualSuggestions([]);
      return;
    }
    manualSearchTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('products')
          .select('id, name, brand, image_url, category')
          .or(`name.ilike.%${value}%,brand.ilike.%${value}%`)
          .limit(6);
        setManualSuggestions(data || []);
      } catch {
        setManualSuggestions([]);
      }
    }, 250);
  };

  const handlePickSuggestion = (p) => {
    setProductName(p.name || '');
    if (p.brand) setBrand(p.brand);
    if (p.image_url) { setImageUrl(p.image_url); setImageRemoved(false); }
    setManualSuggestionsOpen(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-3xl max-h-[88vh] flex flex-col">
        <div className="px-5 pt-5 pb-3 flex items-center gap-3 flex-shrink-0">
          <button onClick={onCancel} className="p-1.5 hover:bg-[#F5F5F5] rounded-xl">
            <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="font-bold text-[#2A1212] text-lg flex-1">{isEditing ? 'Edit product' : 'Add product'}</h3>
        </div>

        {!isEditing && (
          <div className="px-5 pb-4 flex-shrink-0">
            <div className="bg-[#F4F1EC] rounded-full p-1 flex">
              {[['catalog', 'My products'], ['manual', 'Enter manually']].map(([val, lbl]) => (
                <button key={val} onClick={() => setView(val)}
                  className={`flex-1 py-2 rounded-full text-sm font-medium transition-all ${view === val ? 'bg-white text-[#2A1212] shadow-sm' : 'text-[#2A324B]/55'}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-4">
          {view === 'catalog' ? (
            <>
              <div className="relative">
                <svg className="w-4 h-4 text-[#2A324B]/30 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search products…"
                  autoFocus
                  className="w-full bg-[#F5F5F5] rounded-2xl pl-10 pr-4 py-3 text-sm text-[#2A324B] placeholder-[#2A324B]/30 outline-none focus:ring-2 focus:ring-[#F3D5B5]/60" />
              </div>

              {/* Saved products */}
              {savedFiltered.length > 0 && (
                <div className="space-y-1.5">
                  {searchQuery.length > 0 && (
                    <p className="text-xs font-semibold text-[#2A324B]/40 uppercase tracking-wide px-1">Saved</p>
                  )}
                  {savedFiltered.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => handleSelectProduct(p, null)}
                      className="w-full flex items-center gap-3 p-3 rounded-2xl bg-[#F5F5F5] hover:bg-[#F3D5B5]/20 text-left transition-colors">
                      {p.image_url || p.image
                        ? <img src={p.image_url || p.image} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                        : <div className="w-10 h-10 rounded-xl bg-[#E0E0E0] flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-[#2A324B]/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                          </div>
                      }
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-semibold text-[#2A324B] truncate">{p.name || 'Unnamed'}</p>
                          <span className="flex-shrink-0 text-xs bg-[#F3D5B5]/60 text-[#2A324B]/60 px-1.5 py-0.5 rounded-full leading-none">Saved</span>
                        </div>
                        {p.brand && <p className="text-xs text-[#2A324B]/45 truncate">{p.brand}</p>}
                      </div>
                      <svg className="w-4 h-4 text-[#2A324B]/25 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))}
                </div>
              )}

              {/* Catalog search results */}
              {searchQuery.trim().length >= 2 && (
                <div className="space-y-1.5">
                  {savedFiltered.length > 0 && (
                    <p className="text-xs font-semibold text-[#2A324B]/40 uppercase tracking-wide px-1">All Products</p>
                  )}
                  {catalogLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="w-5 h-5 border-2 border-[#F3D5B5] border-t-[#2A324B] rounded-full animate-spin" />
                    </div>
                  ) : catalogResults.length > 0 ? (
                    catalogResults.map(p => {
                      const catId = CATALOG_CATEGORY_MAP[(p.category || '').toLowerCase()];
                      return (
                        <button key={p.id} type="button"
                          onClick={() => handleSelectProduct(p, catId)}
                          className="w-full flex items-center gap-3 p-3 rounded-2xl bg-[#F5F5F5] hover:bg-[#F3D5B5]/20 text-left transition-colors">
                          {p.image_url
                            ? <img src={p.image_url} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                            : <div className="w-10 h-10 rounded-xl bg-[#E0E0E0] flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 text-[#2A324B]/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                              </div>
                          }
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#2A324B] truncate">{p.name || 'Unnamed'}</p>
                            {p.brand && <p className="text-xs text-[#2A324B]/45 truncate">{p.brand}</p>}
                          </div>
                          <svg className="w-4 h-4 text-[#2A324B]/25 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      );
                    })
                  ) : !savedFiltered.length ? (
                    <div className="text-center py-6">
                      <p className="text-sm text-[#2A324B]/40 mb-2">No products found for "{searchQuery}"</p>
                      <button type="button" onClick={() => setView('manual')} className="text-sm text-[#2A324B]/60 underline">Enter manually</button>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Empty state — no search yet */}
              {!searchQuery && !savedFiltered.length && (
                <div className="text-center py-8">
                  <p className="text-sm text-[#2A324B]/40 mb-1">No saved products yet</p>
                  <p className="text-xs text-[#2A324B]/30 mb-3">Search by name or brand to find products</p>
                  <button type="button" onClick={() => setView('manual')} className="text-sm text-[#2A324B]/60 underline">Enter manually</button>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Product name (primary field, leads the form) */}
              <div className="relative">
                <label className="text-[13px] font-semibold text-[#2A1212] block mb-1.5">Product name</label>
                <input
                  value={productName}
                  onChange={e => handleManualNameChange(e.target.value)}
                  onFocus={() => productName.length >= 2 && setManualSuggestionsOpen(true)}
                  onBlur={() => setTimeout(() => setManualSuggestionsOpen(false), 150)}
                  placeholder="e.g. Hydro Boost Water Gel"
                  className="w-full bg-[#FAF7F2] border border-[#EDE8E0] rounded-xl px-3.5 py-3 text-[15px] text-[#2A1212] placeholder-[#2A324B]/35 outline-none focus:ring-2 focus:ring-[#F3D5B5]/60"
                />
                {manualSuggestionsOpen && manualSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-2xl shadow-lg border border-[#E8E8E8] z-10 max-h-60 overflow-y-auto">
                    <p className="text-[13px] font-semibold uppercase tracking-wider text-[#2A324B]/35 px-3 pt-2.5 pb-1">Matches in our catalogue</p>
                    {manualSuggestions.map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => handlePickSuggestion(p)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-[#F5F5F5] text-left transition-colors">
                        {p.image_url
                          ? <img src={p.image_url} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                          : <div className="w-8 h-8 rounded-lg bg-[#F0F0F0] flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#2A324B] truncate">{p.name}</p>
                          {p.brand && <p className="text-xs text-[#2A324B]/50 truncate">{p.brand}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Brand */}
              <div>
                <label className="text-[13px] font-semibold text-[#2A1212] block mb-1.5">Brand <span className="font-normal text-[#2A324B]/40">optional</span></label>
                <input value={brand} onChange={e => setBrand(e.target.value)} placeholder="e.g. Neutrogena"
                  className="w-full bg-[#FAF7F2] border border-[#EDE8E0] rounded-xl px-3.5 py-3 text-[15px] text-[#2A1212] placeholder-[#2A324B]/35 outline-none focus:ring-2 focus:ring-[#F3D5B5]/60" />
              </div>
              {/* Step type — one compact dropdown (was a 10-pill grid) */}
              <div>
                <label className="text-[13px] font-semibold text-[#2A1212] block mb-1.5">Step type</label>
                <div className="relative">
                  <select value={categoryId} onChange={e => setCategoryId(e.target.value)}
                    className="w-full appearance-none bg-[#FAF7F2] border border-[#EDE8E0] rounded-xl px-3.5 py-3 text-[15px] text-[#2A1212] outline-none cursor-pointer focus:ring-2 focus:ring-[#F3D5B5]/60"
                    style={{ WebkitAppearance: 'none' }}>
                    {STEP_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                  <svg className="w-4 h-4 text-[#2A324B]/45 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>

              {/* Photo — small optional affordance (was a big preview + 2 buttons) */}
              <div>
                <label className="text-[13px] font-semibold text-[#2A1212] block mb-1.5">Photo <span className="font-normal text-[#2A324B]/40">optional</span></label>
                {imageUrl ? (
                  <div className="flex items-center gap-3">
                    <img src={imageUrl} alt="" className="w-[52px] h-[52px] rounded-xl object-cover flex-shrink-0 bg-[#F4F1EC]" onError={e => { e.currentTarget.style.display = 'none'; }} />
                    <button type="button" onClick={() => productImageInputRef.current?.click()} disabled={imageUploading} className="text-[13px] font-medium text-[#2A324B]/70 underline">Change</button>
                    <button type="button" onClick={handleClearProductImage} className="text-[13px] text-[#9A5A5A]/80 underline">Remove</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => productImageInputRef.current?.click()} disabled={imageUploading}
                    className="w-full flex items-center gap-3 bg-white border border-dashed border-[#D9D2C6] rounded-xl px-3 py-2.5 hover:bg-[#FAF7F2] transition-colors text-left">
                    <span className="w-[40px] h-[40px] rounded-xl bg-[#F4F1EC] flex items-center justify-center flex-shrink-0">
                      {imageUploading
                        ? <div className="w-4 h-4 border-2 border-[#8A5A2A]/30 border-t-[#8A5A2A] rounded-full animate-spin" />
                        : <svg className="w-5 h-5 text-[#8A5A2A]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
                    </span>
                    <span className="text-[14px] font-medium text-[#2A1212]">{imageUploading ? 'Uploading…' : 'Add a photo'}</span>
                  </button>
                )}
                {imageUploadError && <p className="text-xs text-red-500 mt-2">{imageUploadError}</p>}
                <input ref={productImageInputRef} type="file" accept="image/*" className="hidden" onChange={handleProductImageUpload} />
                <input ref={productImageCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleProductImageUpload} />
              </div>
            </>
          )}
        </div>

        {view === 'manual' && (
          <div className="px-5 pb-8 pt-3 border-t border-[#F0F0F0]">
            <button onClick={() => onConfirm({ categoryId, productName: productName.trim(), brand: brand.trim(), imageUrl, imageRemoved })}
              disabled={!productName.trim()}
              className="w-full bg-[#2A324B] text-[#F3D5B5] py-3.5 rounded-2xl font-semibold text-sm disabled:opacity-40">
              {isEditing ? 'Save Changes' : 'Add to Routine'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Scan Lineup modal ─────────────────────────────────────────────────────────
// After AI identifies products the user can:
//   - Toggle which products to include
//   - Tap the pencil on any row to edit name, brand, and category inline
function ScanLineupModal({ userId, onConfirm, onCancel }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  // Library picker (no `capture` attr) and camera-direct (`capture="environment"`
  // for back camera — product shelves are usually shot with the rear camera).
  // Android needs the explicit camera input to offer Take Photo.
  const attachInputRef = useRef();
  const attachCameraRef = useRef();

  const handleFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null); setError(null); setLoading(true); setEditingIndex(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error('Not authenticated');

      // Compress + base64 in one pass. Raw phone photos (3-12 MB) inflate ~33%
      // in base64 and blow Lambda's 6 MB sync payload cap, which kills the
      // request before it reaches Lambda (no log, just "Scan failed"). The
      // chat upload path does this — replicating here.
      const { compressedBlob, base64: imageBase64 } = await prepareImageForUpload(f);

      const filePath = `uploaded_images/${userId}/${Date.now()}_lineup_${f.name.replace(/[^a-z0-9.]/gi, '_')}`;
      const { error: uploadErr } = await supabase.storage
        .from('images').upload(filePath, compressedBlob, { contentType: 'image/jpeg', upsert: false });
      if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`);
      const { data: signed, error: signErr } = await supabase.storage
        .from('images').createSignedUrl(filePath, 604800);
      if (signErr || !signed?.signedUrl) throw new Error('Could not generate signed URL');

      const resp = await fetch(getChatRequestUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
        body: JSON.stringify({
          message: 'Identify the skincare products in this lineup photo.',
          userId,
          image_data: imageBase64,
          image_url: signed.signedUrl,
          analysis_type: 'product_lineup', // dedicated backend flow: identify + catalog-reconcile
          ephemeral: true, // utility call — keep out of chat history
          accessToken,
        }),
      });

      if (!resp.ok) throw new Error(`Backend ${resp.status}`);
      const data = await resp.json();
      if (!data.success) throw new Error(data.message || 'Failed');

      // Backend returns a structured, catalog-reconciled `products` array.
      // Fall back to regex-parsing `message` for older backend deployments.
      let products = Array.isArray(data.products) ? data.products : null;
      if (!products) {
        const match = (data.message || '').match(/\[[\s\S]*\]/);
        if (match) {
          try { products = JSON.parse(match[0]); } catch { products = null; }
        }
      }
      if (products && products.length) {
        const normalised = products.map(p => ({
          name: p.name || '',
          brand: p.brand || '',
          categoryId: p.categoryId || p.category || 'other',
          catalogId: p.catalogId ?? null,
          catalogImageUrl: p.catalogImageUrl ?? null,
          unconfirmed: p.unconfirmed ?? false,
        }));
        setResult(normalised);
        setSelected(normalised.map((_, i) => i));
      } else {
        setError('Could not identify products. Try a clearer photo.');
      }
    } catch (err) {
      console.error('Scan lineup error:', err);
      setError(`Scan failed — ${err.message || 'please try again'}.`);
    } finally {
      setLoading(false);
    }
  };

  const updateProduct = (index, field, value) => {
    setResult(prev => prev.map((p, i) => i === index
      ? { ...p, [field]: value, ...((field === 'name' || field === 'brand') ? { unconfirmed: false } : {}) }
      : p));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-3xl max-h-[85vh] flex flex-col">
        <div className="px-5 pt-5 pb-4 flex items-center gap-3 flex-shrink-0">
          <button onClick={onCancel} className="p-1.5 hover:bg-[#F5F5F5] rounded-xl">
            <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <h3 className="font-bold text-[#2A324B] text-lg">Log your routine</h3>
            <p className="text-xs text-[#2A324B]/45">Line up products in the order you use them</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-4">
          {!file ? (
            <>
              <div className="border-2 border-dashed border-[#F3D5B5] rounded-2xl p-8 flex flex-col items-center gap-4 text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#F5F5F5] flex items-center justify-center">
                  <svg className="w-8 h-8 text-[#2A324B]/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="font-semibold text-[#2A324B]">Line up in order of use</p>
                {/* Two CTAs — Android users couldn't take a photo before
                    because the file input had no `capture` attribute. */}
                <div className="w-full flex flex-col gap-2 max-w-xs">
                  <button
                    type="button"
                    onClick={() => attachCameraRef.current?.click()}
                    className="bg-[#2A324B] text-[#F3D5B5] px-6 py-3 rounded-full text-sm font-semibold"
                  >
                    Take photo
                  </button>
                  <button
                    type="button"
                    onClick={() => attachInputRef.current?.click()}
                    className="border border-[#2A324B]/20 text-[#2A324B] px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#FDF8F4] transition-colors"
                  >
                    Choose from library
                  </button>
                </div>
              </div>
              <div className="bg-[#FDF6EE] border border-[#F3D5B5]/40 rounded-2xl p-4">
                <p className="font-semibold text-sm text-[#2A324B] mb-2">For best results</p>
                <ul className="text-sm text-[#2A324B]/60 space-y-1">
                  <li>• Arrange products in the order you use them</li>
                  <li>• Labels forward, good even lighting</li>
                  <li>• Switch tabs (AM / PM) to log each separately</li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <div className="relative aspect-video bg-[#F5F5F5] rounded-2xl">
                <div className="absolute inset-0 rounded-2xl overflow-hidden">
                  {preview && <img src={preview} alt="Lineup" className="w-full h-full object-cover" />}
                  {loading && (
                    <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-3">
                      <div className="w-8 h-8 border-2 border-[#F3D5B5] border-t-white rounded-full animate-spin" />
                      <p className="text-white text-sm font-medium">Identifying products…</p>
                    </div>
                  )}
                </div>
                {!loading && (
                  <button type="button"
                    onClick={() => { setFile(null); setPreview(null); setResult(null); setEditingIndex(null); setSelected([]); }}
                    className="absolute top-3 right-3 z-10 bg-black/50 rounded-full p-1.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {error && (
                <div className="bg-red-50 rounded-xl p-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {result && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-[#2A324B]">
                      Found {result.length} product{result.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-[#2A324B]/40">Tap pencil to correct</p>
                  </div>
                  {result.map((p, i) => {
                    const cat = getCat(p.categoryId);
                    const isOn = selected.includes(i);
                    const isExpanded = editingIndex === i;
                    return (
                      <div key={i} className={`rounded-2xl border transition-all ${isOn ? 'border-[#2A324B]/15 bg-[#F8F8F8]' : 'border-[#E0E0E0] bg-white opacity-50'}`}>
                        {/* Row */}
                        <div className="flex items-center gap-3 p-3">
                          <button type="button"
                            onClick={() => setSelected(prev => isOn ? prev.filter(x => x !== i) : [...prev, i])}
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isOn ? 'border-[#2A324B] bg-[#2A324B]' : 'border-[#D0D0D0]'}`}>
                            {isOn && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                          {p.catalogImageUrl
                            ? <img src={p.catalogImageUrl} alt="" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" onError={e => { e.currentTarget.style.display='none'; e.currentTarget.nextSibling.style.display='flex'; }} />
                            : null}
                          <span className="w-9 h-9 rounded-xl bg-[#F4F1EC] flex items-center justify-center flex-shrink-0" style={p.catalogImageUrl ? {display:'none'} : {}}>
                            <svg className="w-4 h-4 text-[#2A324B]/30" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 3h4v2.2l1.2 2.1A2 2 0 0116 8.4V19a2 2 0 01-2 2h-4a2 2 0 01-2-2V8.4a2 2 0 01.8-1.6L10 5.2V3z" /></svg>
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-semibold text-[#2A324B] truncate">{p.name}</p>
                              {p.unconfirmed && (
                                <span className="flex-shrink-0 text-[0.6rem] font-semibold uppercase tracking-wide text-[#B26B00] bg-[#FBE6CF] rounded-full px-1.5 py-0.5">
                                  Check
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-[#2A324B]/45">{p.brand ? `${p.brand} · ` : ''}{cat.label}</p>
                          </div>
                          {/* Edit toggle */}
                          <button type="button"
                            onClick={() => setEditingIndex(isExpanded ? null : i)}
                            className="p-1.5 hover:bg-[#F0F0F0] rounded-lg transition-colors flex-shrink-0">
                            <svg className={`w-4 h-4 transition-colors ${isExpanded ? 'text-[#2A324B]' : 'text-[#2A324B]/25'}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>

                        {/* Inline editor */}
                        {isExpanded && (
                          <div className="px-3 pb-3 pt-2 space-y-2 border-t border-[#F0F0F0]">
                            <input value={p.name} onChange={e => updateProduct(i, 'name', e.target.value)}
                              placeholder="Product name"
                              className="w-full bg-white border border-[#E0E0E0] rounded-xl px-3 py-2 text-sm text-[#2A324B] outline-none focus:border-[#2A324B]/40" />
                            <input value={p.brand || ''} onChange={e => updateProduct(i, 'brand', e.target.value)}
                              placeholder="Brand (optional)"
                              className="w-full bg-white border border-[#E0E0E0] rounded-xl px-3 py-2 text-sm text-[#2A324B] outline-none focus:border-[#2A324B]/40" />
                            <div className="flex flex-wrap gap-1.5">
                              {STEP_CATEGORIES.map(c => (
                                <button key={c.id} type="button" onClick={() => updateProduct(i, 'categoryId', c.id)}
                                  className={`px-2.5 py-1 rounded-full border text-[13px] font-medium transition-all ${
                                    p.categoryId === c.id ? 'border-[#2A324B] bg-[#2A324B] text-white' : 'border-[#E8E8E8] text-[#2A324B]/50 hover:border-[#2A324B]/30'
                                  }`}>
                                  {c.label}
                                </button>
                              ))}
                            </div>
                            <button type="button" onClick={() => setEditingIndex(null)}
                              className="w-full py-2 bg-[#2A324B] text-[#F3D5B5] rounded-xl text-sm font-semibold">
                              Done
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {result && selected.length > 0 && (
          <div className="px-5 pb-8 pt-3 border-t border-[#F0F0F0]">
            <button onClick={() => onConfirm(result.filter((_, i) => selected.includes(i)))}
              className="w-full bg-[#2A324B] text-[#F3D5B5] py-3.5 rounded-2xl font-semibold text-sm">
              Add {selected.length} product{selected.length !== 1 ? 's' : ''} to Routine
            </button>
          </div>
        )}
        {/* Two attach inputs — library (no capture) and camera-direct (capture
            attr). Android needs the explicit camera input; iOS works with both. */}
        <input ref={attachInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
        <input ref={attachCameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} />
      </div>
    </div>
  );
}

// ── Routine steps list ────────────────────────────────────────────────────────
// - No emoji in empty step placeholder (just the label text)
// - Filled steps show category type as a muted label next to the brand
function StepList({ steps, onStepEdit, onStepRemove, onStepReorder, routineLabel, savedProducts = [] }) {
  const filledCount = steps.filter(s => s.productName).length;
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const dragIndexRef = useRef(null);
  const overIndexRef = useRef(null);

  // Pointer-events drag: works on mouse, touch and pen. Replaces HTML5
  // drag-and-drop because that API doesn't fire on mobile Safari (it shows a
  // "drop image" overlay instead — the bug we saw in the routine on phone).
  const handlePointerDown = (i) => (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    // Android Chrome retargets or pointercancels a drag the moment it decides the
    // gesture might be a scroll, which is what made this stutter and drop out on
    // Android while feeling fine on iOS. Capturing the pointer pins the whole
    // gesture to this handle until we release it.
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (_e) {}
    // The row under the finger IS the row being dragged, so elementFromPoint kept
    // returning it and the drop target never updated until you moved a long way.
    // Make it transparent to hit-testing for the duration of the drag.
    const draggedRow = e.currentTarget.closest('[data-step-index]');
    if (draggedRow) draggedRow.style.pointerEvents = 'none';
    dragIndexRef.current = i;
    overIndexRef.current = i;
    setDragIndex(i);
    setOverIndex(i);

    const handleMove = (ev) => {
      const clientX = ev.clientX ?? ev.touches?.[0]?.clientX;
      const clientY = ev.clientY ?? ev.touches?.[0]?.clientY;
      if (clientX == null || clientY == null) return;
      const el = document.elementFromPoint(clientX, clientY);
      const row = el?.closest('[data-step-index]');
      if (!row) return;
      const newOver = Number(row.getAttribute('data-step-index'));
      if (!Number.isNaN(newOver) && newOver !== overIndexRef.current) {
        overIndexRef.current = newOver;
        setOverIndex(newOver);
      }
    };

    const handleUp = () => {
      if (draggedRow) draggedRow.style.pointerEvents = '';
      const from = dragIndexRef.current;
      const to = overIndexRef.current;
      if (from != null && to != null && from !== to) {
        onStepReorder(from, to);
      }
      dragIndexRef.current = null;
      overIndexRef.current = null;
      setDragIndex(null);
      setOverIndex(null);
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
  };

  return (
    <div>
      {/* Steps — flat rows (no card); filled steps get a soft highlight (widget look) */}
      {steps.map((step, i) => {
        const cat = getCat(step.categoryId);
        const hasProduct = !!step.productName;
        const isDragging = dragIndex === i;
        const isOver = overIndex === i && dragIndex !== null && dragIndex !== i;
        // Older routine steps use '' for both "no photo" and "removed photo".
        // Respect the explicit flag for new edits; otherwise try a matching saved
        // product before leaving the row empty.
        const match = savedProducts.find(p =>
          p.name?.trim().toLowerCase() === step.productName?.trim().toLowerCase() &&
          (!step.brand || !p.brand || p.brand.trim().toLowerCase() === step.brand.trim().toLowerCase()));
        const imgUrl = hasProduct && !step.imageRemoved
          ? (step.imageUrl || match?.catalog_image_url || match?.image_url || match?.image || null)
          : null;
        return (
          <div
            key={step.id}
            data-step-index={i}
            className={`flex items-center gap-2 transition-colors ${isDragging ? 'opacity-40' : ''} ${hasProduct ? 'px-2 py-2 my-1 rounded-2xl bg-[#FCFAF6]' : `px-2 py-2.5 ${i < steps.length - 1 ? 'border-b border-[#F4F1EC]' : ''}`} ${isOver ? 'bg-[#F3D5B5]/15 rounded-2xl' : ''}`}>
            {/* Drag handle — subtle grip, no step number */}
            <span
              onPointerDown={handlePointerDown(i)}
              className="flex-shrink-0 -ml-0.5 cursor-grab active:cursor-grabbing text-[#2A324B]/20 hover:text-[#2A324B]/45 transition-colors touch-none select-none"
              title="Drag to reorder"
              style={{ touchAction: 'none' }}
            >
              <svg className="w-2 h-4 pointer-events-none" viewBox="0 0 8 16" fill="currentColor">
                <circle cx="2.5" cy="4" r="0.9" /><circle cx="2.5" cy="8" r="0.9" /><circle cx="2.5" cy="12" r="0.9" />
                <circle cx="5.5" cy="4" r="0.9" /><circle cx="5.5" cy="8" r="0.9" /><circle cx="5.5" cy="12" r="0.9" />
              </svg>
            </span>

            {hasProduct ? (
              <>
                <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-[#F4F1EC] relative flex items-center justify-center text-[#2A324B]/35">
                  <Package2 className="w-5 h-5" aria-hidden="true" />
                  {imgUrl && <img src={imgUrl} alt="" className="absolute inset-0 w-full h-full object-cover"
                    onError={e => { e.currentTarget.style.display = 'none'; }} />}
                </div>
                <button type="button" onClick={() => onStepEdit(step, i)} className="flex-1 min-w-0 text-left">
                  <p className="text-[10.5px] font-semibold text-[#2A324B]/50 uppercase tracking-wide leading-none mb-1">{cat.label}</p>
                  <p className="text-[15px] font-semibold text-[#2A1212] leading-snug"
                    style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {stripPackSuffix(step.productName)}
                  </p>
                  {step.brand && <p className="text-[13px] text-[#2A324B]/50 truncate leading-tight mt-0.5">{step.brand}</p>}
                </button>
                {/* Frequency chip — muted warm pill on the right. Native select for the iOS wheel. */}
                {/* Value-sized pill: visible label sets the width (a bare <select>
                    balloons to its longest option); a transparent select overlays it. */}
                <div className="relative inline-flex items-center flex-shrink-0 bg-[#FBF1E4] rounded-full px-3 py-1">
                  <span className="text-[13px] font-medium text-[#8A5A2A] whitespace-nowrap">{step.frequency}</span>
                  <select
                    value={step.frequency}
                    onChange={e => onStepEdit({ ...step, frequency: e.target.value }, i, true)}
                    onClick={e => e.stopPropagation()}
                    aria-label="Frequency"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    style={{ WebkitAppearance: 'none' }}
                  >
                    {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <button type="button" onClick={() => onStepRemove(i)}
                  className="flex-shrink-0 p-1 text-[#2A324B]/20 hover:text-[#9A5A5A] transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => onStepEdit(step, i)} className="flex items-center gap-2.5 flex-1 min-w-0 text-left">
                <div className="w-[64px] h-[64px] rounded-xl flex-shrink-0 flex items-center justify-center border-[1.5px] border-dashed border-[#E5E0D8]">
                  <svg className="w-4 h-4 text-[#2A324B]/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-medium text-[#2A1212]">{cat.label}</p>
                  <p className="text-[13px] text-[#2A324B]/40">Tap to add a product</p>
                </div>
                </button>
                {/* Empty slots were the only rows with no way out: the remove button
                    lived inside the filled branch, so a suggested step you don't use
                    (a toner you don't own) sat in your routine permanently. */}
                <button type="button" onClick={() => onStepRemove(i)}
                  aria-label={`Remove ${cat.label} step`}
                  className="flex-shrink-0 p-1 text-[#2A324B]/20 hover:text-[#9A5A5A] transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </>
            )}
          </div>
        );
      })}

    </div>
  );
}

// ── Main SkinRoutine ──────────────────────────────────────────────────────────
// Persists to `skin_routine_data` table (user_id PK, routines JSONB).
// Migration: create_skin_routine_data (already applied to ernsdcoqdwwutczluuue).
export default function SkinRoutine({ userId, isAnonymous = false, onShowSignIn, enrichedProducts = [], addProduct = null, onProductAdded }) {
  // Building/tracking a routine is a logged-in feature. Guests can view, but any
  // add/scan action prompts sign-in. Returns true if the action was blocked.
  const requireLogin = () => {
    if (isAnonymous) { onShowSignIn?.(); return true; }
    return false;
  };
  const [routines, setRoutines] = useState(INITIAL_ROUTINES);
  const [activeId, setActiveId] = useState('daily');
  const [subTab, setSubTab] = useState('morning');
  const [showAddInline, setShowAddInline] = useState(false);
  const [showRoutineMenu, setShowRoutineMenu] = useState(false); // Daily ⌄ switcher (#41)
  const [editingRoutineId, setEditingRoutineId] = useState(null); // rename current routine
  const [newRoutineName, setNewRoutineName] = useState('');
  const [newRoutineTimeOfDay, setNewRoutineTimeOfDay] = useState(false);
  const [editingStep, setEditingStep] = useState(null); // { step, index, stepsKey }
  const [showScan, setShowScan] = useState(false);
  const [savedProducts, setSavedProducts] = useState([]);
  const [loadedProducts, setLoadedProducts] = useState(false);
  const [dbLoading, setDbLoading] = useState(true);
  const [saveIndicator, setSaveIndicator] = useState(null); // 'saving' | 'saved' | null
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [routineAnalysis, setRoutineAnalysis] = useState(null);
  const [analyzeError, setAnalyzeError] = useState(null);

  const saveTimerRef = useRef(null);
  const indicatorTimerRef = useRef(null);
  const isInitialLoad = useRef(true);
  // Last-saved routine snapshot — used to compute per-save diffs so today's
  // journal entry accumulates every change instead of recording only the first.
  const lastSavedRoutinesRef = useRef(null);

  const activeRoutine = routines.find(r => r.id === activeId);

  // ── Load routines + saved products from Supabase on mount ──
  useEffect(() => {
    if (!userId) {
      setDbLoading(false);
      isInitialLoad.current = false;
      return;
    }
    (async () => {
      try {
        const [{ data: routineData }, { data: productsData }] = await Promise.all([
          supabase.from('skin_routine_data').select('routines').eq('user_id', userId).single(),
          supabase.from('saved_products').select('id, name, brand, image_url, image').eq('user_id', userId).order('created_at', { ascending: false }),
        ]);
        if (routineData?.routines && Array.isArray(routineData.routines) && routineData.routines.length > 0) {
          let loaded = routineData.routines;
          // #43: backfill missing step thumbnails from the catalog. Steps added
          // without a photo store imageUrl '' (SkinRoutine.jsx:1450), which StepList
          // treats as "explicitly cleared" and renders blank — so most routine cards
          // showed no image. Resolve those blanks to a fresh catalog hero by exact
          // product name (productName is stored verbatim from the catalog on add, so
          // an exact `.in()` match is reliable). Best-effort + in-memory only; the
          // diff baseline below is seeded with the enriched copy so this never counts
          // as a user edit. Steps that already carry a real (user-uploaded) photo are
          // left untouched by the `!s.imageUrl` guard.
          try {
            const needImg = [...new Set(
              loaded
                .flatMap(r => [...(r.morningSteps || []), ...(r.eveningSteps || []), ...(r.steps || [])])
                .filter(s => s && s.productName && !s.imageUrl && !s.imageRemoved)
                .map(s => s.productName)
            )];
            if (needImg.length > 0) {
              const { data: catRows } = await supabase
                .from('products')
                .select('name, image_url')
                .in('name', needImg);
              const imgByName = {};
              (productsData || []).forEach(row => {
                const url = row.image_url || row.image;
                if (url && row.name && !imgByName[row.name]) imgByName[row.name] = url;
              });
              (catRows || []).forEach(row => {
                if (row.image_url && !imgByName[row.name]) imgByName[row.name] = row.image_url;
              });
              if (Object.keys(imgByName).length > 0) {
                const fill = (s) =>
                  s && s.productName && !s.imageUrl && !s.imageRemoved && imgByName[s.productName]
                    ? { ...s, imageUrl: imgByName[s.productName] }
                    : s;
                loaded = loaded.map(r => ({
                  ...r,
                  ...(r.morningSteps ? { morningSteps: r.morningSteps.map(fill) } : {}),
                  ...(r.eveningSteps ? { eveningSteps: r.eveningSteps.map(fill) } : {}),
                  ...(r.steps ? { steps: r.steps.map(fill) } : {}),
                }));
              }
            }
          } catch (_) {
            // Enrichment is best-effort; blank thumbnails are acceptable.
          }
          // Stored upload photos use short-lived signed URLs. Renew them when
          // reopening a routine, including photos saved on older devices.
          const refreshStep = async (step) => {
            const url = step?.imageUrl;
            if (!url || typeof url !== 'string' || !url.includes('supabase.co/storage')) return step;
            const match = url.match(/\/object\/sign\/images\/(.+?)\?/);
            if (!match) return step;
            try {
              const { data, error } = await supabase.storage.from('images').createSignedUrl(match[1], 604800);
              if (!error && data?.signedUrl) return { ...step, imageUrl: data.signedUrl };
            } catch (_) {}
            return step;
          };
          loaded = await Promise.all(loaded.map(async routine => ({
            ...routine,
            ...(routine.morningSteps ? { morningSteps: await Promise.all(routine.morningSteps.map(refreshStep)) } : {}),
            ...(routine.eveningSteps ? { eveningSteps: await Promise.all(routine.eveningSteps.map(refreshStep)) } : {}),
            ...(routine.steps ? { steps: await Promise.all(routine.steps.map(refreshStep)) } : {}),
          })));
          setRoutines(loaded);
          // Seed the diff baseline with what's in the DB (the enriched copy) so the
          // first edit diffs against actual prior state rather than INITIAL_ROUTINES
          // (which would emit spurious "removed" events), and so the image backfill
          // above never registers as a user edit.
          lastSavedRoutinesRef.current = loaded;
          // Restore active tab to first routine if it no longer exists
          if (!loaded.find(r => r.id === activeId)) {
            setActiveId(loaded[0].id);
          }
          // Backfill "Currently Using" from the routine. The per-add sync only
          // covers products added after that feature shipped; this ensures
          // products already in the routine show up too (deduped, add-only).
          if (!isAnonymous) {
            const allSteps = loaded.flatMap(r =>
              [...(r.morningSteps || []), ...(r.eveningSteps || []), ...(r.steps || [])]
            );
            syncToCurrentlyUsing(allSteps);
          }
        } else {
          lastSavedRoutinesRef.current = routines;
        }
        // Refresh any expired Supabase signed URLs so product images never go stale
        const refreshed = await Promise.all(
          (productsData || []).map(async (p) => {
            const url = p.image_url || p.image;
            if (!url || typeof url !== 'string' || !url.includes('supabase.co/storage')) return p;
            const match = url.match(/\/object\/sign\/images\/(.+?)\?/);
            if (!match) return p;
            try {
              const { data, error } = await supabase.storage
                .from('images').createSignedUrl(match[1], 604800);
              if (!error && data?.signedUrl) {
                return p.image_url
                  ? { ...p, image_url: data.signedUrl }
                  : { ...p, image: data.signedUrl };
              }
            } catch (_) {}
            return p;
          })
        );
        setSavedProducts(refreshed);
        setLoadedProducts(true);
      } catch {
        // Table may not exist yet or no saved data — use defaults
      } finally {
        setDbLoading(false);
        setTimeout(() => { isInitialLoad.current = false; }, 50);
      }
    })();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Summarise a routine set for journal entries
  const summariseRoutines = (rs) => {
    try {
      const parts = [];
      rs.forEach(r => {
        const stepGroups = r.hasTimeOfDay
          ? [{ label: 'AM', steps: r.morningSteps }, { label: 'PM', steps: r.eveningSteps }]
          : [{ label: '', steps: r.steps }];
        stepGroups.forEach(({ label, steps }) => {
          const named = (steps || []).filter(s => s.productName);
          if (!named.length) return;
          const list = named.map(s => s.productName).join(', ');
          parts.push(`${r.name}${label ? ' (' + label + ')' : ''}: ${list}`);
        });
      });
      return parts.join(' · ') || 'Routine updated';
    } catch {
      return 'Routine updated';
    }
  };

  // Diff two routine snapshots and emit user-meaningful change events.
  // Each event has: kind, routine, timeOfDay (null if single-list), step,
  // and either product/brand (for *_added / *_removed) or from/to (for
  // *_changed). Plus an ISO `at` timestamp so the timeline can render order.
  const diffRoutines = (prev, next) => {
    const events = [];
    const now = new Date().toISOString();
    const stepCategoryLabel = (id) => (STEP_CATEGORIES.find(c => c.id === id)?.label) || id || 'Step';

    const indexSteps = (rs) => {
      // Map: routineId -> { name, timeBuckets: [{ tod, stepsById }] }
      const out = new Map();
      (rs || []).forEach(r => {
        const buckets = r.hasTimeOfDay
          ? [{ tod: 'AM', steps: r.morningSteps || [] }, { tod: 'PM', steps: r.eveningSteps || [] }]
          : [{ tod: null, steps: r.steps || [] }];
        out.set(r.id, {
          name: r.name || 'Routine',
          buckets: buckets.map(b => ({
            tod: b.tod,
            stepsById: new Map((b.steps || []).map(s => [s.id, s])),
          })),
        });
      });
      return out;
    };

    const prevIdx = indexSteps(prev);
    const nextIdx = indexSteps(next);

    // Iterate all routine ids across both snapshots
    const allRoutineIds = new Set([...prevIdx.keys(), ...nextIdx.keys()]);
    for (const rid of allRoutineIds) {
      const p = prevIdx.get(rid);
      const n = nextIdx.get(rid);
      const routineName = n?.name || p?.name || 'Routine';

      // Whole routine added or removed → skip per-step diff (would be noisy)
      if (!p && n) {
        // emit a single "routine added" via product_added for each filled step
        n.buckets.forEach(({ tod, stepsById }) => {
          for (const s of stepsById.values()) {
            if (s.productName) {
              events.push({
                kind: 'product_added', routine: routineName, timeOfDay: tod,
                step: stepCategoryLabel(s.categoryId), product: s.productName,
                brand: s.brand || null, at: now,
              });
            }
          }
        });
        continue;
      }
      if (p && !n) {
        p.buckets.forEach(({ tod, stepsById }) => {
          for (const s of stepsById.values()) {
            if (s.productName) {
              events.push({
                kind: 'product_removed', routine: routineName, timeOfDay: tod,
                step: stepCategoryLabel(s.categoryId), product: s.productName,
                brand: s.brand || null, at: now,
              });
            }
          }
        });
        continue;
      }

      // Both exist: diff per bucket
      const pBuckets = new Map(p.buckets.map(b => [b.tod, b.stepsById]));
      const nBuckets = new Map(n.buckets.map(b => [b.tod, b.stepsById]));
      const tods = new Set([...pBuckets.keys(), ...nBuckets.keys()]);

      for (const tod of tods) {
        const pSteps = pBuckets.get(tod) || new Map();
        const nSteps = nBuckets.get(tod) || new Map();
        const stepIds = new Set([...pSteps.keys(), ...nSteps.keys()]);

        for (const sid of stepIds) {
          const ps = pSteps.get(sid);
          const ns = nSteps.get(sid);
          const stepLabel = stepCategoryLabel(ns?.categoryId || ps?.categoryId);

          if (!ps && ns) {
            // New step row — only log if it has a product (empty placeholder isn't interesting)
            if (ns.productName) {
              events.push({
                kind: 'product_added', routine: routineName, timeOfDay: tod,
                step: stepLabel, product: ns.productName, brand: ns.brand || null, at: now,
              });
            }
            continue;
          }
          if (ps && !ns) {
            if (ps.productName) {
              events.push({
                kind: 'product_removed', routine: routineName, timeOfDay: tod,
                step: stepLabel, product: ps.productName, brand: ps.brand || null, at: now,
              });
            }
            continue;
          }
          // Both exist — compare product / brand / frequency
          const pProd = (ps.productName || '').trim();
          const nProd = (ns.productName || '').trim();
          if (pProd !== nProd) {
            if (!pProd && nProd) {
              events.push({
                kind: 'product_added', routine: routineName, timeOfDay: tod,
                step: stepLabel, product: nProd, brand: ns.brand || null, at: now,
              });
            } else if (pProd && !nProd) {
              events.push({
                kind: 'product_removed', routine: routineName, timeOfDay: tod,
                step: stepLabel, product: pProd, brand: ps.brand || null, at: now,
              });
            } else {
              events.push({
                kind: 'product_changed', routine: routineName, timeOfDay: tod,
                step: stepLabel, from: pProd, to: nProd, at: now,
              });
            }
          } else if (pProd && nProd && ps.frequency !== ns.frequency) {
            events.push({
              kind: 'frequency_changed', routine: routineName, timeOfDay: tod,
              step: stepLabel, product: nProd, from: ps.frequency || '—', to: ns.frequency || '—', at: now,
            });
          }
        }
      }
    }

    return events;
  };

  // ── Auto-save (debounced 800ms) ──
  // Persists the routine state, then journals an entry for today. Multiple
  // edits on the same day collapse into a single skin_journal_entries row —
  // we look up today's existing routine_change entry (if any) and append the
  // new diff events to its routine_changes array. First edit of the day
  // creates a fresh row.
  const saveRoutines = useCallback(async (routinesToSave) => {
    if (!userId) return;
    setSaveIndicator('saving');
    try {
      await supabase
        .from('skin_routine_data')
        .upsert({ user_id: userId, routines: routinesToSave, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });

      // Skip journaling on the initial DB-hydration save (no real change to log)
      if (!isInitialLoad.current) {
        const prev = lastSavedRoutinesRef.current || [];
        const newEvents = diffRoutines(prev, routinesToSave);

        if (newEvents.length > 0) {
          try {
            // Find today's existing routine_change entry (if any)
            const startOfDay = new Date();
            startOfDay.setHours(0, 0, 0, 0);
            const { data: existing } = await supabase
              .from('skin_journal_entries')
              .select('id, routine_changes')
              .eq('user_id', userId)
              .eq('source', 'routine_change')
              .gte('created_at', startOfDay.toISOString())
              .order('created_at', { ascending: false })
              .limit(1);

            const summary = summariseRoutines(routinesToSave);
            if (existing && existing.length > 0) {
              const merged = [
                ...(Array.isArray(existing[0].routine_changes) ? existing[0].routine_changes : []),
                ...newEvents,
              ];
              await supabase
                .from('skin_journal_entries')
                .update({ routine_changes: merged, ai_summary: summary })
                .eq('id', existing[0].id);
            } else {
              await supabase.from('skin_journal_entries').insert({
                user_id: userId,
                source: 'routine_change',
                ai_summary: summary,
                routine_changes: newEvents,
                notes: null,
                photo_paths: [],
                skin_metrics: {},
                overall_score: null,
              });
            }
          } catch (err) {
            // best-effort; don't block routine save
            console.warn('Failed to journal routine change:', err);
          }
        }
        lastSavedRoutinesRef.current = routinesToSave;
      }

      setSaveIndicator('saved');
      if (indicatorTimerRef.current) clearTimeout(indicatorTimerRef.current);
      indicatorTimerRef.current = setTimeout(() => setSaveIndicator(null), 2000);
    } catch {
      setSaveIndicator(null);
    }
  }, [userId]);

  useEffect(() => {
    if (isInitialLoad.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveRoutines(routines), 800);
    return () => clearTimeout(saveTimerRef.current);
  }, [routines, saveRoutines]);

  // ── Step key helpers ──
  const getStepsKey = () => {
    if (!activeRoutine) return null;
    if (activeRoutine.hasTimeOfDay) return subTab === 'morning' ? 'morningSteps' : 'eveningSteps';
    return 'steps';
  };

  const getCurrentSteps = () => {
    if (!activeRoutine) return [];
    const key = getStepsKey();
    return activeRoutine[key] || [];
  };

  const updateSteps = (key, updater) => {
    setRoutines(prev => prev.map(r => {
      if (r.id !== activeId) return r;
      const curr = r[key] || [];
      return { ...r, [key]: typeof updater === 'function' ? updater(curr) : updater };
    }));
  };

  // Mirror routine products into the "Currently Using" saved-products list.
  // Add-only: we never delete here, so analyses/edits the user made there are
  // preserved. The routine itself stays the source of truth for what's actually
  // in use; this just keeps the products list populated without manual re-entry.
  const syncToCurrentlyUsing = useCallback(async (items) => {
    if (!userId || !items?.length) return;
    const candidates = items
      .map(p => ({
        name: (p.productName || p.name || '').trim(),
        brand: (p.brand || '').trim(),
        categoryId: p.categoryId || 'other',
        // Fall back to 'Daily' — that's the default frequency every routine
        // step starts with, so a freshly-added product without an explicit
        // frequency should populate Usage as 'Daily' rather than empty.
        frequency: (p.frequency || 'Daily').trim(),
      }))
      .filter(p => p.name);
    if (!candidates.length) return;
    const key = (n, b) => `${n.trim().toLowerCase()}|${(b || '').trim().toLowerCase()}`;
    try {
      const { data: existing } = await supabase
        .from('saved_products')
        .select('name, brand')
        .eq('user_id', userId)
        .eq('list_category', 'current');
      const seen = new Set((existing || []).map(p => key(p.name || '', p.brand || '')));
      const rows = [];
      for (const c of candidates) {
        const k = key(c.name, c.brand);
        if (seen.has(k)) continue;
        seen.add(k);
        rows.push({
          user_id: userId,
          name: c.name,
          brand: c.brand,
          category: getCat(c.categoryId).label,
          list_category: 'current',
          ...(c.frequency ? { usage: c.frequency } : {}),
        });
      }
      if (rows.length) await supabase.from('saved_products').insert(rows);
    } catch (e) {
      console.error('syncToCurrentlyUsing failed (non-critical):', e);
    }
  }, [userId]);

  // A stable fingerprint of what is actually IN the routine. The analysis only
  // changes when the products change, so this is what decides a cache hit.
  // Record whether the routine has anything in it, so chat can label its CTA
  // without a network round trip just to choose a word.
  useEffect(() => {
    try {
      const filled = routines.some(r =>
        [...(r.morningSteps || []), ...(r.eveningSteps || []), ...(r.steps || [])]
          .some(st => st.productName));
      localStorage.setItem('sonmi_routine_filled', filled ? '1' : '0');
    } catch (_e) {}
  }, [routines]);

  const routineFingerprint = useCallback(() => {
    const r = routines.find(x => x.id === activeId);
    if (!r) return '';
    const all = [...(r.morningSteps || []), ...(r.eveningSteps || []), ...(r.steps || [])];
    return all.filter(st => st.productName)
      .map(st => `${st.categoryId}:${st.brand || ''}:${st.productName}`)
      .join('|');
  }, [routines, activeId]);

  // Stage 2: ask the backend how the routine's products work together.
  // `force` skips the cache (the Refresh affordance).
  const analyzeRoutine = async (force = false) => {
    if (analyzeLoading) return;
    const fp = routineFingerprint();
    const cacheKey = `sonmi_routine_analysis_${activeId}`;
    if (!force) {
      try {
        const raw = localStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached.fp === fp && cached.analysis) {
            // Re-analysing an unchanged routine costs a full tool loop and shows
            // the user the same answer, so serve it instantly instead.
            setAnalyzeError(null);
            setRoutineAnalysis(cached.analysis);
            return;
          }
        }
      } catch (_e) {}
    }
    setAnalyzeError(null);
    setRoutineAnalysis(null);
    setAnalyzeLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      const resp = await fetch(getChatRequestUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
        body: JSON.stringify({ analyzeRoutine: true, userId, accessToken }),
      });
      if (!resp.ok) throw new Error(`Backend ${resp.status}`);
      const data = await resp.json();
      if (!data.success) throw new Error(data.message || 'Analysis failed');
      setRoutineAnalysis(data.analysis);
      try {
        localStorage.setItem(`sonmi_routine_analysis_${activeId}`,
          JSON.stringify({ fp, analysis: data.analysis, at: Date.now() }));
      } catch (_e) {}
    } catch (err) {
      console.error('Routine analysis error:', err);
      setAnalyzeError('Could not analyse your routine. Please try again.');
    } finally {
      setAnalyzeLoading(false);
    }
  };

  // ── Step handlers ──
  const handleStepEdit = (stepOrUpdated, index, isFrequencyOnly = false) => {
    if (requireLogin()) return;
    const key = getStepsKey();
    if (isFrequencyOnly) {
      updateSteps(key, prev => prev.map((s, i) => i === index ? { ...s, frequency: stepOrUpdated.frequency } : s));
      return;
    }
    setEditingStep({ step: stepOrUpdated || null, index: index ?? null, stepsKey: key });
  };

  const handleStepConfirm = ({ categoryId, productName, brand, imageUrl, imageRemoved }) => {
    const { index, stepsKey } = editingStep;
    // Always write `imageUrl` so '' (user explicitly removed) overrides any
    // previously-stored URL. Previous code's `...(imageUrl ? { imageUrl } : {})`
    // dropped the field on empty, which silently preserved the old value —
    // making "Remove photo" appear to do nothing.
    if (index === null) {
      updateSteps(stepsKey, prev => [...prev, {
        id: `${categoryId}_${Date.now()}`,
        categoryId, productName, brand, frequency: 'Daily',
        imageUrl: imageUrl || '', imageRemoved: !!imageRemoved,
      }]);
    } else {
      updateSteps(stepsKey, prev => prev.map((s, i) => i === index ? { ...s, categoryId, productName, brand, imageUrl: imageUrl || '', imageRemoved: !!imageRemoved } : s));
    }
    if (productName) syncToCurrentlyUsing([{ productName, brand, categoryId }]);
    setEditingStep(null);
  };

  const handleStepRemove = (index) => {
    updateSteps(getStepsKey(), prev => prev.filter((_, i) => i !== index));
  };

  const handleStepReorder = (from, to) => {
    updateSteps(getStepsKey(), prev => {
      if (from < 0 || from >= prev.length || to < 0 || to >= prev.length) return prev;
      const copy = [...prev];
      const [moved] = copy.splice(from, 1);
      copy.splice(to, 0, moved);
      return copy;
    });
  };

  const handleScanConfirm = (products) => {
    // SCAN ORDER WINS. People photograph their products in the order they use
    // them, and that sequence is information — it's how we can later tell them
    // they're applying something in the wrong place. Slot-filling by category
    // silently reordered it into our canonical order and destroyed that signal.
    // So: keep the scanned sequence, and drop the empty placeholder slots the
    // scan has just covered, which is what made them feel like leftovers.
    updateSteps(getStepsKey(), prev => {
      const scannedCats = new Set(products.map(p => p.categoryId || 'other'));
      const kept = prev.filter(st => st.productName || !scannedCats.has(st.categoryId));
      const added = products.map(p => ({
        id: `${p.categoryId || 'other'}_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`,
        categoryId: p.categoryId || 'other',
        productName: p.name || '',
        brand: p.brand || '',
        frequency: 'Daily',
        ...(p.catalogId ? { catalogId: p.catalogId } : {}),
        ...(p.catalogImageUrl ? { imageUrl: p.catalogImageUrl } : {}),
      }));
      return [...kept, ...added];
    });
    syncToCurrentlyUsing(products);
    setShowScan(false);
  };

  // Auto-insert a product from the Shelf ("Add to my routine", #36). Maps its catalog
  // category to a slot; fills the first empty matching slot, else appends. Lands on AM.
  const addProductToRoutine = useCallback((prod) => {
    if (!prod) return;
    const categoryId = CATALOG_CATEGORY_MAP[String(prod.category || '').toLowerCase().trim()] || 'other';
    const img = prod.image || prod.catalog_image_url || '';
    const filled = { productName: prod.name || '', brand: prod.brand || '', ...(img ? { imageUrl: img } : {}) };
    setRoutines(prev => prev.map(r => {
      if (r.id !== activeId) return r;
      const key = r.hasTimeOfDay ? 'morningSteps' : 'steps';
      const steps = r[key] || [];
      const emptyIdx = steps.findIndex(s => s.categoryId === categoryId && !s.productName);
      const next = emptyIdx >= 0
        ? steps.map((s, i) => (i === emptyIdx ? { ...s, ...filled } : s))
        : [...steps, { id: `${categoryId}_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`, categoryId, frequency: 'Daily', ...filled }];
      return { ...r, [key]: next };
    }));
    if (activeRoutine?.hasTimeOfDay) setSubTab('morning');
  }, [activeId, activeRoutine]);

  // Fire the insert once, AFTER routines have loaded (else the async load clobbers it).
  const lastAddRef = useRef(null);
  useEffect(() => {
    if (!addProduct) { lastAddRef.current = null; return; }
    if (dbLoading || lastAddRef.current === addProduct) return;
    lastAddRef.current = addProduct;
    addProductToRoutine(addProduct);
    onProductAdded?.();
  }, [addProduct, dbLoading, addProductToRoutine, onProductAdded]);

  // ── Routine management ──
  const createRoutine = () => {
    const name = newRoutineName.trim();
    if (!name) return;
    const newR = {
      id: `r_${Date.now()}`,
      name,
      isDaily: false,
      hasTimeOfDay: newRoutineTimeOfDay,
      ...(newRoutineTimeOfDay
        ? { morningSteps: [], eveningSteps: [] }
        : { steps: [] }),
    };
    setRoutines(prev => [...prev, newR]);
    setActiveId(newR.id);
    setSubTab('morning');
    setNewRoutineName('');
    setNewRoutineTimeOfDay(false);
    setShowAddInline(false);
  };

  // Rename the current routine (header Edit affordance) + apply any AM/PM change.
  const renameRoutine = () => {
    const name = newRoutineName.trim();
    if (!name || !editingRoutineId) return;
    setRoutines(prev => prev.map(r => {
      if (r.id !== editingRoutineId) return r;
      let next = { ...r, name };
      if (!r.isDaily && !!r.hasTimeOfDay !== newRoutineTimeOfDay) {
        if (r.hasTimeOfDay) {
          const merged = [...(r.morningSteps || []), ...(r.eveningSteps || [])];
          const { morningSteps, eveningSteps, ...rest } = next;
          next = { ...rest, hasTimeOfDay: false, steps: merged };
        } else {
          const { steps, ...rest } = next;
          next = { ...rest, hasTimeOfDay: true, morningSteps: steps || [], eveningSteps: [] };
        }
      }
      return next;
    }));
    setEditingRoutineId(null);
    setNewRoutineName('');
    setNewRoutineTimeOfDay(false);
    setShowAddInline(false);
    setSubTab('morning');
  };

  // Toggle AM/PM split on an existing custom routine
  const toggleTimeOfDay = () => {
    setRoutines(prev => prev.map(r => {
      if (r.id !== activeId || r.isDaily) return r;
      if (r.hasTimeOfDay) {
        // Collapse to single list — merge morning + evening
        const merged = [...(r.morningSteps || []), ...(r.eveningSteps || [])];
        const { morningSteps, eveningSteps, ...rest } = r;
        return { ...rest, hasTimeOfDay: false, steps: merged };
      } else {
        // Expand to AM/PM — move existing steps to morning
        const { steps, ...rest } = r;
        return { ...rest, hasTimeOfDay: true, morningSteps: steps || [], eveningSteps: [] };
      }
    }));
    setSubTab('morning');
  };

  const deleteRoutine = (id) => {
    if (id === 'daily') return;
    setRoutines(prev => prev.filter(r => r.id !== id));
    setActiveId('daily');
  };

  // ── Derived values ──
  const steps = getCurrentSteps();
  const routineLabel = !activeRoutine ? '' :
    activeRoutine.isDaily
      ? (subTab === 'morning' ? 'Morning Routine' : 'Evening Routine')
      : activeRoutine.hasTimeOfDay
        ? (subTab === 'morning' ? `${activeRoutine.name} — Morning` : `${activeRoutine.name} — Evening`)
        : `${activeRoutine.name} Routine`;

  if (dbLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-[#F3D5B5] border-t-[#2A324B] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4 pb-6">

        {isAnonymous && (
          <div className="rounded-2xl bg-[#F7EFE6] border border-[#E8D7C7] px-4 py-3.5">
            <p className="text-sm font-semibold text-[#2A1212]">Preview your routine here</p>
            <p className="text-sm text-[#2A324B]/70 leading-relaxed mt-1">
              Create a free account when you are ready to add products, scan your lineup and keep Son:mi's compatibility checks.
            </p>
            <button
              type="button"
              onClick={() => onShowSignIn?.()}
              className="mt-2.5 text-sm font-semibold text-[#9C6045] underline underline-offset-2"
            >
              Create account or sign in
            </button>
          </div>
        )}

        {/* Scan moved to the bottom action row (Add step / Scan) */}

        {/* ── Save indicator ────────────────────────────────────────── */}
        {saveIndicator && (
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
            saveIndicator === 'saving'
              ? 'bg-[#F5F5F5] text-[#2A324B]/50'
              : 'bg-[#F0FDF4] text-green-700'
          }`}>
            {saveIndicator === 'saving' ? (
              <>
                <div className="w-3 h-3 border border-[#2A324B]/30 border-t-[#2A324B]/60 rounded-full animate-spin flex-shrink-0" />
                Saving…
              </>
            ) : (
              <>
                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                Saved
              </>
            )}
          </div>
        )}

        {/* ── Routine header: name switcher + edit (widget look, #41) ── */}
        <div className="flex items-center justify-between relative">
          <button type="button" onClick={() => setShowRoutineMenu(v => !v)} className="flex items-center gap-1.5">
            <span className="text-[20px] font-bold text-[#2A1212]">{activeRoutine?.name || 'Daily'}</span>
            <svg className="w-4 h-4 text-[#2A324B]/45 transition-transform" style={{ transform: showRoutineMenu ? 'rotate(180deg)' : 'none' }}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
          {activeRoutine && !showAddInline && (
            <button type="button"
              onClick={() => { setEditingRoutineId(activeId); setNewRoutineName(activeRoutine.name); setNewRoutineTimeOfDay(!!activeRoutine.hasTimeOfDay); setShowAddInline(true); setShowRoutineMenu(false); }}
              className="text-[13px] font-medium text-[#2A324B]/55 hover:text-[#2A1212] transition-colors">
              Edit
            </button>
          )}
          {showRoutineMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowRoutineMenu(false)} />
              <div className="absolute left-0 top-full mt-2 z-20 min-w-[190px] bg-white rounded-xl border border-[#E5E0D8] py-1" style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.10)' }}>
                {routines.map(r => (
                  <button key={r.id} type="button"
                    onClick={() => { setActiveId(r.id); setShowAddInline(false); setSubTab('morning'); setShowRoutineMenu(false); }}
                    className="w-full flex items-center justify-between px-4 py-2 text-[14px] text-left hover:bg-[#FAF7F2]">
                    <span className={activeId === r.id ? 'font-semibold text-[#2A1212]' : 'text-[#2A324B]/70'}>{r.name}</span>
                    {activeId === r.id && (<svg className="w-3.5 h-3.5 text-[#2A1212]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>)}
                  </button>
                ))}
                <div className="border-t border-[#F1ECE4] mt-1 pt-1">
                  <button type="button"
                    onClick={() => { setEditingRoutineId(null); setNewRoutineName(''); setNewRoutineTimeOfDay(false); setShowAddInline(true); setShowRoutineMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-[14px] text-left text-[#2A324B]/70 hover:bg-[#FAF7F2]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    New routine
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Inline new routine form ───────────────────────────────── */}
        {showAddInline && (
          <div className="bg-[#FDF6EE] border border-[#F3D5B5]/60 rounded-2xl p-4 space-y-3">
            <p className="font-semibold text-[#2A324B] text-sm">{editingRoutineId ? 'Edit routine name' : 'Name your routine'}</p>
            <input
              value={newRoutineName}
              onChange={e => setNewRoutineName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createRoutine()}
              placeholder="e.g. Exfoliation, Barrier Repair…"
              autoFocus
              className="w-full bg-white rounded-xl px-4 py-2.5 text-sm text-[#2A324B] placeholder-[#2A324B]/30 outline-none border border-[#F3D5B5]/40 focus:border-[#F3D5B5]" />
            {/* Quick-pick suggestions */}
            <div className="flex flex-wrap gap-2">
              {NAME_SUGGESTIONS.map(s => (
                <button key={s} type="button" onClick={() => setNewRoutineName(s)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    newRoutineName === s
                      ? 'bg-[#2A324B] text-[#F3D5B5] border-[#2A324B]'
                      : 'bg-white text-[#2A324B]/60 border-[#E0E0E0] hover:border-[#2A324B]/30'
                  }`}>
                  {s}
                </button>
              ))}
            </div>
            {/* AM/PM split option */}
            <button type="button" onClick={() => setNewRoutineTimeOfDay(v => !v)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                newRoutineTimeOfDay ? 'border-[#2A324B]/30 bg-[#F8F8F8]' : 'border-[#E0E0E0] bg-white'
              }`}>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${newRoutineTimeOfDay ? 'border-[#2A324B] bg-[#2A324B]' : 'border-[#D0D0D0]'}`}>
                {newRoutineTimeOfDay && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-[#2A324B]">Split into AM / PM steps</p>
                <p className="text-xs text-[#2A324B]/40">Separate morning and evening products</p>
              </div>
            </button>
            <div className="flex gap-2 pt-1">
              <button onClick={() => { setShowAddInline(false); setNewRoutineName(''); setNewRoutineTimeOfDay(false); setEditingRoutineId(null); }}
                className="flex-1 py-2.5 rounded-xl border border-[#E0E0E0] text-sm font-semibold text-[#2A324B]/60 hover:bg-[#F5F5F5] transition-colors">
                Cancel
              </button>
              <button onClick={editingRoutineId ? renameRoutine : createRoutine} disabled={!newRoutineName.trim()}
                className="flex-1 py-2.5 rounded-xl bg-[#2A324B] text-[#F3D5B5] text-sm font-semibold disabled:opacity-40 transition-opacity">
                {editingRoutineId ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        )}

        {/* ── Morning / Evening — underline tabs (Shelf look, Sofia-approved) ── */}
        {activeRoutine?.hasTimeOfDay && !showAddInline && (
          <div className="flex items-center gap-6 border-b border-[#EDE8E0] px-1">
            <button onClick={() => setSubTab('morning')}
              className="py-2.5 text-[14px] transition-colors"
              style={subTab === 'morning'
                ? { fontWeight: 600, color: '#2A1212', borderBottom: '2px solid #2A1212', marginBottom: '-1px' }
                : { fontWeight: 500, color: 'rgba(42,33,18,0.4)' }}>
              Morning
            </button>
            <button onClick={() => setSubTab('evening')}
              className="py-2.5 text-[14px] transition-colors"
              style={subTab === 'evening'
                ? { fontWeight: 600, color: '#2A1212', borderBottom: '2px solid #2A1212', marginBottom: '-1px' }
                : { fontWeight: 500, color: 'rgba(42,33,18,0.4)' }}>
              Evening
            </button>
            <span className="ml-auto text-[13px] text-[#2A324B]/45">{steps.filter(s => s.productName).length} of {steps.length}</span>
          </div>
        )}

        {/* ── Step list (flat rows) ────────────────────────────────── */}
        {!showAddInline && activeRoutine && (
          <StepList
            steps={steps}
            routineLabel={routineLabel}
            onStepEdit={handleStepEdit}
            onStepRemove={handleStepRemove}
            onStepReorder={handleStepReorder}
            savedProducts={enrichedProducts.length > 0 ? enrichedProducts : savedProducts}
          />
        )}

        {/* ── Add step / Scan — bottom action row (widget look) ─────── */}
        {!showAddInline && activeRoutine && (
          <div className="flex gap-2.5">
            <button onClick={() => handleStepEdit(null, null)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-[#D9D2C6] bg-white text-[13px] font-semibold text-[#2A1212] hover:bg-[#FAF7F2] transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add step
            </button>
            <button onClick={() => { if (!requireLogin()) setShowScan(true); }}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[#2A1212] text-[13px] font-semibold text-[#F3D5B5] hover:bg-[#3A2020] transition-colors">
              <svg className="w-4 h-4 text-[#F3D5B5]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              Scan
            </button>
          </div>
        )}

        {/* ── Stage 2: routine compatibility analysis ─────────────── */}
        {!showAddInline && activeRoutine && steps.filter(s => s.productName).length >= 2 && (
          <button
            onClick={analyzeRoutine}
            disabled={analyzeLoading}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#FDF6EE] border border-[#F3D5B5]/60 hover:bg-[#F9EEDF] transition-colors text-left disabled:opacity-60"
          >
            <div className="w-9 h-9 rounded-xl bg-[#F3D5B5]/50 flex items-center justify-center flex-shrink-0">
              {analyzeLoading ? (
                <div className="w-4 h-4 border-2 border-[#A8742F]/30 border-t-[#A8742F] rounded-full animate-spin" />
              ) : (
                <svg className="w-5 h-5 text-[#A8742F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#2A324B]">{analyzeLoading ? 'Analysing your routine…' : 'How does my routine work together?'}</p>
              <p className="text-[13px] text-[#2A324B]/60 leading-snug mt-0.5">Son:mi checks your products for conflicts, synergies and timing.</p>
            </div>
          </button>
        )}

        {/* ── AM/PM split toggle for custom routines ──────────────── */}
        {!showAddInline && activeRoutine && !activeRoutine.isDaily && (
          <button onClick={toggleTimeOfDay}
            className="w-full flex items-center gap-3 py-3 px-4 rounded-2xl border border-dashed border-[#E0E0E0] hover:border-[#2A324B]/20 hover:bg-[#FAFAFA] transition-colors text-left">
            <svg className="w-4 h-4 text-[#2A324B]/30 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {activeRoutine.hasTimeOfDay
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707" />
              }
            </svg>
            <span className="text-sm text-[#2A324B]/50 font-medium">
              {activeRoutine.hasTimeOfDay ? 'Remove AM / PM split' : 'Split into AM / PM steps'}
            </span>
          </button>
        )}

        {/* ── Delete custom routine ────────────────────────────────── */}
        {!showAddInline && activeRoutine && !activeRoutine.isDaily && (
          <button onClick={() => deleteRoutine(activeId)}
            className="w-full py-2.5 text-sm text-red-400 hover:text-red-500 font-medium transition-colors">
            Delete this routine
          </button>
        )}
      </div>

      {/* ── Modals ────────────────────────────────────────────────── */}
      {editingStep && (
        <StepModal
          step={editingStep.step}
          userId={userId}
          savedProducts={enrichedProducts.length > 0 ? enrichedProducts : savedProducts}
          onConfirm={handleStepConfirm}
          onCancel={() => setEditingStep(null)}
        />
      )}
      {showScan && (
        <ScanLineupModal
          userId={userId}
          onConfirm={handleScanConfirm}
          onCancel={() => setShowScan(false)}
        />
      )}
      {(routineAnalysis || analyzeError) && (
        <RoutineAnalysisModal
          onRefresh={() => analyzeRoutine(true)}
          analysis={routineAnalysis}
          error={analyzeError}
          onClose={() => { setRoutineAnalysis(null); setAnalyzeError(null); }}
        />
      )}
    </>
  );
}
